/**
 * Auth module — thin shim over @wg/hooks/auth core. Keeps the same export
 * surface the rest of the app already imports (getAuthToken, signin, etc).
 * SSO (Google) is web-specific and lives here.
 */
import { auth, httpClient, unauthClient } from '../client';
import type { SessionInfo } from '@wg/hooks/auth';

const AUTH_API = '/functions/v1/auth';

export const getAuthToken = () => auth.getAuthToken();
export const tryRefreshAuthToken = () => auth.tryRefreshAuthToken();
export const getSessionInfo = () => auth.getSessionInfo();
export const clearAuthSession = () => auth.clearAuthSession();
export const signin = (payload: { emailAddress: string; password: string }) =>
  auth.signin(payload);
export const register = (payload: {
  emailAddress: string;
  password: string;
  firstName: string;
  lastName: string;
  language: string;
}) => auth.register(payload);
export const logout = () => auth.logout();
export const deleteAccount = () => auth.deleteAccount();
export const createGuestSession = () => auth.createGuestSession();
export const requestPasswordReset = (emailAddress: string, language?: string) =>
  auth.requestPasswordReset(emailAddress, language);
export const confirmPasswordReset = (payload: {
  code: string;
  newPassword: string;
}) => auth.confirmPasswordReset(payload);
export const onAuthEvent = auth.onAuthEvent;

/**
 * Sign in with Google ID token (mirrors wg-portal pattern).
 * The /sso/google endpoint accepts an authed (guest) or unauthed client; on
 * success we adopt the returned tokens via setSessionFromExternal.
 */
export async function signInWithGoogle(idToken: string) {
  const sessionInfo = await auth.getSessionInfo();
  const client = sessionInfo.token ? httpClient : unauthClient;
  const { data } = await client.post(`${AUTH_API}/sso/google`, {
    id_token: idToken,
  });
  const accessToken = data.access_token ?? data.accessToken;
  const refreshToken = data.refresh_token ?? data.refreshToken;
  const expiresAt = data.expires_at ?? data.expiresAt;
  const isNewUser = data.is_new_user ?? data.isNewUser ?? false;

  await auth.setSessionFromExternal({
    accessToken,
    refreshToken,
    expiresAt: Number(expiresAt),
  });
  return { isNewUser };
}

export type { SessionInfo };
